import { Injectable } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

@Injectable({ providedIn: 'root' })
export class SafeAreaService {
  private inited = false;
  /** Last StatusBar.setOverlaysWebView preference (welcome uses true). */
  private overlaysWebView = false;

  async init() {
    if (this.inited) return;
    this.inited = true;

    if (Capacitor.getPlatform() === 'web') return; // <— skip on web

    try {
      // ✅ Make sure content is laid out below status bar
      this.overlaysWebView = false;
      await StatusBar.setOverlaysWebView({ overlay: false });


      const bgColor = this.resolveStatusBarBackgroundColor();

      // Set status bar background
      await StatusBar.setBackgroundColor({ color: bgColor });

      // Pick icon style for contrast
      const rgb = this.colorToRgb(bgColor);
      const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
      const style = brightness > 150 ? Style.Light : Style.Dark;

      await StatusBar.setStyle({ style });

      // Ensure top safe-area inset matches status bar height (no overlap)
      document.documentElement.style.setProperty(
        '--safe-area-inset-top',
        `${this.getStatusBarHeight()}px`
      );
    } catch (err) {
      console.warn('SafeAreaService init error', err);
    }

    // Keyboard handling
    try {
      Keyboard.addListener('keyboardWillShow', (info) => {
        document.documentElement.style.setProperty('--keyboard-inset', `${info.keyboardHeight}px`);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        document.documentElement.style.setProperty('--keyboard-inset', `0px`);
      });
      Keyboard.addListener('keyboardDidShow', (info) => {
        document.documentElement.style.setProperty('--keyboard-inset', `${info.keyboardHeight || 0}px`);
      });
      Keyboard.addListener('keyboardDidHide', () => {
        document.documentElement.style.setProperty('--keyboard-inset', `0px`);
      });
    } catch { }
  }

  private colorToRgb(color: string): { r: number; g: number; b: number } {
    const hex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (hex) {
      return {
        r: parseInt(hex[1], 16),
        g: parseInt(hex[2], 16),
        b: parseInt(hex[3], 16),
      };
    }
    const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(color);
    if (rgb) {
      return {
        r: parseInt(rgb[1], 10),
        g: parseInt(rgb[2], 10),
        b: parseInt(rgb[3], 10),
      };
    }
    return { r: 0, g: 0, b: 0 };
  }

  private rgbToHex(r: number, g: number, b: number): string {
    const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  // Approximate status bar height for safe-area inset (iOS + Android)
  private getStatusBarHeight(): number {
    if (/iphone|ipod|ipad/i.test(navigator.userAgent)) {
      // Common iOS status bar heights
      return window.screen.height >= 812 ? 44 : 20;
    }
    return 24; // Default Android dp
  }

  /** Top inset (status bar / notch) in px for positioning native overlays. Web returns 0. */
  public getTopInsetPx(): number {
    if (Capacitor.getPlatform() === 'web') {
      return 0;
    }

    const fromCss = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top').trim()
    );
    if (Number.isFinite(fromCss) && fromCss > 0) {
      return fromCss;
    }

    return this.getStatusBarHeight();
  }

  public async setWelcomeColour() {
     if (Capacitor.getPlatform() === 'web') return; // <— skip on web
    this.overlaysWebView = true;
    await StatusBar.setOverlaysWebView({ overlay: true });


  }

  public async defaultColour() {
    if (Capacitor.getPlatform() === 'web') return;
    this.overlaysWebView = false;
    await StatusBar.setOverlaysWebView({ overlay: false });
  }

  /**
   * Re-apply status bar and top safe area after returning from a native view
   * (camera, in-app browser / privacy policy, etc.).
   * Restores the last overlay preference so welcome stays edge-to-edge.
   * @param options.skipFlicker - When true, only reapply color/overlay/inset (no hide/show). Use for delayed follow-up calls to avoid multiple flickers.
   */
  public async reapplyAfterNativeView(options?: { skipFlicker?: boolean }): Promise<void> {
    if (Capacitor.getPlatform() === 'web') return;
    const skipFlicker = options?.skipFlicker === true;
    try {
      const bgColor = this.resolveStatusBarBackgroundColor();
      const rgb = this.colorToRgb(bgColor);
      const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
      const style = brightness > 150 ? Style.Light : Style.Dark;

      if (!skipFlicker && Capacitor.getPlatform() === 'ios') {
        await StatusBar.setBackgroundColor({ color: bgColor });
        await StatusBar.setStyle({ style });
        await StatusBar.hide();
        await new Promise((r) => setTimeout(r, 80));
        await StatusBar.show();
      }

      await StatusBar.setOverlaysWebView({ overlay: this.overlaysWebView });
      if (!this.overlaysWebView) {
        const topPx = this.getStatusBarHeight();
        document.documentElement.style.setProperty('--safe-area-inset-top', `${topPx}px`);
      } else {
        document.documentElement.style.removeProperty('--safe-area-inset-top');
      }
      this.forceLayoutRefresh();
      await StatusBar.setBackgroundColor({ color: bgColor });
      await StatusBar.setStyle({ style });
    } catch (err) {
      console.warn('SafeAreaService reapplyAfterNativeView error', err);
    }
  }

  /**
   * Resolve flavour --viva-status-bar-color (home top) to a hex StatusBar can use.
   * getPropertyValue returns unresolved `var(...)` strings, so probe computed style.
   */
  private resolveStatusBarBackgroundColor(): string {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;' +
      'background-color:var(--viva-status-bar-color,var(--viva-home-background-start,var(--ion-background-color,#0d152d)));';
    document.documentElement.appendChild(probe);
    const computed = getComputedStyle(probe).backgroundColor;
    probe.remove();

    const rgb = this.colorToRgb(computed);
    if (computed && computed !== 'rgba(0, 0, 0, 0)' && computed !== 'transparent') {
      return this.rgbToHex(rgb.r, rgb.g, rgb.b);
    }
    return '#0d152d';
  }

  /** Force the webview to recalculate layout (reflow + resize event). */
  private forceLayoutRefresh(): void {
    void document.body.offsetHeight;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('resize'));
    }
  }
}
